import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/products';

function CategoryCard({ cat, index }: { cat: typeof categories[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="group relative overflow-hidden cursor-pointer"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.7s ease ${index * 0.1}s, transform 0.7s ease ${index * 0.1}s`,
      }}
    >
      <Link to={`/category/${cat.id}`} className="block">
        {/* Image */}
        <div className="relative h-80 md:h-96 overflow-hidden">
          <img
            src={cat.image}
            alt={cat.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C]/80 via-[#1C1C1C]/20 to-transparent"></div>

          {/* Top badge */}
          <div className="absolute top-5 left-5 bg-white/90 backdrop-blur-sm px-3 py-1.5 text-[10px] font-bold tracking-[0.25em] uppercase text-[#1C1C1C]">
            {cat.count}+ პროდუქტი
          </div>

          {/* Bottom content */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 flex items-center justify-center bg-[#ec3145]">
                <i className={`${cat.icon} text-white text-sm`}></i>
              </div>
              <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/50">
                {cat.brands.join(', ')}
              </span>
            </div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-white mb-2 group-hover:text-[#ec3145] transition-colors duration-300">
              {cat.name}
            </h3>
            <div className="flex items-center gap-2 text-sm font-semibold text-white/70 group-hover:text-white transition-colors duration-300">
              <span>ყველა პროდუქტის ნახვა</span>
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function CategoriesPage() {
  return (
    <div className="font-sans bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[480px] md:min-h-[560px] bg-[#1C1C1C] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Premium%20construction%20materials%20showroom%20interior%20with%20marble%20tiles%20wood%20flooring%20and%20stone%20samples%20displayed%20elegantly%20warm%20golden%20lighting%20luxury%20architecture%20photography%20wide%20angle&width=1920&height=800&seq=phoenix-cat-hero&orientation=landscape"
            alt="პროდუქტის კატეგორიები"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1C] via-[#1C1C1C]/80 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-20">
          <div className="max-w-2xl">
            <span className="inline-block text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] mb-4">
              კატალოგი
            </span>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              პროდუქტის<br />
              <span className="text-[#ec3145]">კატეგორიები</span>
            </h1>
            <p className="text-base md:text-lg text-white/60 leading-relaxed max-w-lg">
              აირჩიეთ თქვენი პროექტისთვის შესაფერისი კატეგორია და გაეცანით მსოფლიოს წამყვანი ბრენდების პრემიუმ პროდუქტებს.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="bg-[#F5F2EE] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {categories.map((cat, i) => (
              <CategoryCard key={cat.id} cat={cat} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1C1C1C] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
            ვერ იპოვეთ სასურველი კატეგორია?
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto mb-8">
            ჩვენი გუნდი დაგეხმარებათ სწორი მასალების შერჩევაში. დაგვიკავშირდით კონსულტაციისთვის.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/shop"
              className="inline-flex items-center justify-center gap-2 bg-[#ec3145] text-white font-semibold px-8 py-4 hover:bg-[#c9283a] transition-colors duration-300 whitespace-nowrap"
            >
              <i className="ri-shopping-bag-line"></i>
              მაღაზიაში გადასვლა
            </Link>
            <a
              href="tel:+995599872020"
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-semibold px-8 py-4 hover:bg-white/10 transition-colors duration-300 whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              +995 599 87 20 20
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}